import React, { useState, useRef, useCallback } from 'react';
import { X, Loader2, Navigation, Save, Check, Clock, Route as RouteIcon, Search, MapPin, Plus, GripVertical, LocateFixed, ChevronUp, AlertTriangle, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppUser, Coordinates, RoutePlanAlternative, SavedTrack } from '../types';

import { searchPlace } from '../services/geocodingService';
import { formatDuration } from '../services/routingService';
import { BRAND } from '../constants/mapColors';

const ROUTE_COLORS = [BRAND, '#3b82f6', '#f59e0b'];

interface RoutePlanBarProps {
  isDesktop: boolean;
  alternatives: RoutePlanAlternative[];
  selectedIndex: number | null;
  onSelectIndex: (index: number) => void;
  toName: string;
  fromName: string;
  fromCoords: Coordinates;
  toCoords: Coordinates;
  waypoints: { coords: Coordinates; name: string }[];
  onAddWaypoint: (coords: Coordinates, name: string) => void;
  onRemoveWaypoint: (index: number) => void;
  onReorderWaypoints: (waypoints: { coords: Coordinates; name: string }[]) => void;
  onChangeFrom: (coords: Coordinates, name: string) => void;
  onResetFrom: () => void;
  onChangeTo: (coords: Coordinates, name: string) => void;
  user: AppUser;
  fetching: boolean;
  error: string | null;
  onClose: () => void;
  onSaved: (track: SavedTrack, routeId?: string | null) => void;
  onNavigate: (alternative: RoutePlanAlternative) => void;
  onSaveRequested: (alternative: RoutePlanAlternative, name: string) => void;
  onShowRouteContent?: () => void;
  /** Valgfri: gjør et stopp-navn trykkbart (åpner plass-kort). Brukes av reise-copiloten
   *  der hvert waypoint svarer til en ekte plass. Utelatt for manuell ruteplan. */
  onWaypointTap?: (index: number) => void;
  /** Reise-copilot: utforskningsmodus. Når satt blir «Utforsk ruten» PRIMÆR handling
   *  (åpner copilotens kuraterte innhold) og «Start Navigering» demoteres til sekundær —
   *  så det letteste trykket aldri kaster brukeren inn i GPS-sporing. */
  onExploreContent?: () => void;
}

type SearchTarget = 'stop' | 'from' | 'to';

const RoutePlanBar: React.FC<RoutePlanBarProps> = ({
  isDesktop,
  alternatives, selectedIndex, onSelectIndex,
  toName, fromName, fromCoords, toCoords,
  waypoints, onAddWaypoint, onRemoveWaypoint, onReorderWaypoints,
  onChangeFrom, onResetFrom, onChangeTo,
  user, fetching, error, onClose, onSaved, onNavigate, onSaveRequested, onShowRouteContent, onWaypointTap, onExploreContent
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [searchTarget, setSearchTarget] = useState<SearchTarget | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState(true);

  // Build default name from waypoints
  const defaultName = React.useMemo(() => {
    const parts = [fromName];
    waypoints.forEach(w => parts.push(w.name));
    parts.push(toName);
    return parts.filter(Boolean).map(p => {
      const comma = p.indexOf(',');
      return comma > 0 ? p.substring(0, comma) : p;
    }).join(' → ');
  }, [fromName, toName, waypoints]);

  const prevDefaultRef = React.useRef(defaultName);
  React.useEffect(() => {
    const prev = prevDefaultRef.current;
    prevDefaultRef.current = defaultName;
    // Oppdater rutenavnet hvis brukeren ikke har redigert det manuelt
    if (!routeName || routeName === prev) setRouteName(defaultName);
  }, [defaultName]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 3) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      // Nordisk: la brukeren endre til svenske/danske steder også (ikke bare Norge).
      const results = await searchPlace(query, ['no', 'se', 'dk']);
      setSearchResults(results);
    }, 300);
  }, []);

  const selectSearchResult = (r: any) => {
    const coords = { latitude: parseFloat(r.lat), longitude: parseFloat(r.lon) };
    const name = r.display_name;
    if (searchTarget === 'from') {
      onChangeFrom(coords, name);
    } else if (searchTarget === 'to') {
      onChangeTo(coords, name);
    } else {
      onAddWaypoint(coords, name);
    }
    setSearchQuery('');
    setSearchResults([]);
    setSearchTarget(null);
  };

  const openSearch = (target: SearchTarget) => {
    setSearchTarget(target);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSave = () => {
    if (selectedIndex === null || !alternatives.length) return;
    const alt = alternatives[selectedIndex];
    onSaveRequested(alt, routeName || defaultName);
  };

  // Drag-to-reorder handlers
  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const reordered = [...waypoints];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    onReorderWaypoints(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Touch drag support
  const touchStartY = useRef<number | null>(null);
  const touchStartIndex = useRef<number | null>(null);

  const handleTouchMove = useCallback((index: number, startY: number, e: React.TouchEvent) => {
    const dy = e.touches[0].clientY - startY;
    const chipHeight = 32;
    const offset = Math.round(dy / chipHeight);
    const targetIdx = Math.max(0, Math.min(waypoints.length - 1, index + offset));
    setDragOverIndex(targetIdx);
  }, [waypoints.length]);

  const handleTouchEnd = useCallback((index: number) => {
    if (dragOverIndex !== null && dragOverIndex !== index) {
      const reordered = [...waypoints];
      const [moved] = reordered.splice(index, 1);
      reordered.splice(dragOverIndex, 0, moved);
      onReorderWaypoints(reordered);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    touchStartY.current = null;
    touchStartIndex.current = null;
  }, [dragOverIndex, waypoints, onReorderWaypoints]);

  const hasAlternatives = alternatives.length > 1;
  const selectedAlt = selectedIndex !== null ? alternatives[selectedIndex] : null;

  // Collapsed summary for mobile
  const collapsedSummary = React.useMemo(() => {
    const from = fromName.split(',')[0] || 'Start';
    const to = toName.split(',')[0];
    if (!selectedAlt) return { label: `${from} → ${to}`, distance: '', duration: '' };
    return {
      label: `${from} → ${to}`,
      distance: `${(selectedAlt.distanceMeters / 1000).toFixed(0)} km`,
      duration: formatDuration(selectedAlt.durationSeconds),
    };
  }, [fromName, toName, selectedAlt]);

  return (
    <div
      className={
        isDesktop
          ? "absolute bottom-0 left-4 z-[3000] pointer-events-auto w-80 max-w-[calc(100vw-2rem)]"
          : "fixed bottom-0 left-0 right-0 z-[3500] pointer-events-auto animate-in slide-in-from-bottom-4"
      }
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {/* Mobile collapsed strip */}
      {!isDesktop && !mobileExpanded && (
        <div
          onClick={() => setMobileExpanded(true)}
          className="bg-[var(--bg-surface)] border-t border-x border-[var(--border-secondary)] rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.4)] cursor-pointer"
        >
          <div className="w-full flex justify-center pt-3 pb-1">
            <div className="w-12 h-1.5 bg-[var(--text-muted)]/30 rounded-full" />
          </div>
          <div className="flex items-center justify-between px-5 pb-4">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <Navigation size={14} className="text-brand shrink-0" />
              <span className="text-xs font-black text-[var(--text-primary)] truncate">
                {collapsedSummary.label}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              {collapsedSummary.distance && (
                <div className="flex items-center gap-3 text-tiny font-black text-[var(--text-secondary)]">
                  <span className="flex items-center gap-0.5">
                    <RouteIcon size={11} /> {collapsedSummary.distance}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Clock size={11} /> {collapsedSummary.duration}
                  </span>
                </div>
              )}
              {fetching && <Loader2 size={14} className="animate-spin text-brand" />}
              <ChevronUp size={16} className="text-[var(--text-muted)]" />
            </div>
          </div>
        </div>
      )}

      {/* Main content: always visible on desktop, only when expanded on mobile */}
      {(isDesktop || mobileExpanded) && (
      <div className={
        isDesktop
          ? "mb-2 bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-3xl shadow-2xl overflow-hidden"
          : "bg-[var(--bg-surface)] border-t border-x border-[var(--border-secondary)] rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.4)] overflow-hidden max-h-[75dvh] flex flex-col"
      }>
        {/* Mobile drag handle — tap to collapse */}
        {!isDesktop && (
          <div className="w-full flex justify-center pt-3 pb-1 cursor-pointer shrink-0" onClick={() => setMobileExpanded(false)}>
            <div className="w-12 h-1.5 bg-[var(--text-muted)]/30 rounded-full" />
          </div>
        )}

        {/* Scrollable content area on mobile */}
        <div className={!isDesktop ? "overflow-y-auto flex-1" : ""} style={!isDesktop ? { overscrollBehavior: 'contain' } : undefined}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Navigation size={14} className="text-brand shrink-0" />
            <span className="text-xs font-black text-[var(--text-primary)] uppercase tracking-widest truncate">
              {t('routePlanner.route_to', { name: toName.split(',')[0] })}
            </span>
          </div>
          <button onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-[var(--bg-surface-alt)] text-[var(--text-muted)] shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Route points — vertical list: Fra → Stopp 1..N → Mål */}
        <div className="px-4 pb-2 space-y-1.5">
          {/* Fra (clickable to change start) */}
          <div className="flex items-center gap-2 group">
            <div className="w-5 flex justify-center shrink-0">
              <div className="w-3 h-3 rounded-full bg-brand border-2 border-[var(--bg-surface)] shadow" />
            </div>
            <button
              onClick={() => openSearch('from')}
              className="flex-1 min-w-0 flex items-center justify-between px-3 py-2 bg-[var(--bg-input)] hover:bg-[var(--bg-input-hover)] border border-[var(--border-secondary)] hover:border-brand/50 rounded-xl text-left transition group shadow-sm"
              title={t("auto.routeplanbar.title.klikk_for_a_endre_startsted", "Klikk for å endre startsted")}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Search size={12} className="text-brand shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest leading-none mb-0.5">
                    {t('routePlanner.from_label', 'Fra (Startsted)')}
                  </div>
                  <div className="text-xs font-bold text-[var(--text-primary)] truncate">
                    {fromName.split(',')[0] || 'Min posisjon'}
                  </div>
                </div>
              </div>
              <span className="shrink-0 ml-2 px-2 py-1 rounded-lg bg-brand/10 text-brand-dark dark:text-emerald-400 text-2xs font-bold group-hover:bg-brand/20 transition-colors">
                {t("auto.routeplanbar.endre", "Endre")}
              </span>
            </button>
            {fromName !== 'Min posisjon' && (
              <button
                onClick={onResetFrom}
                title={t("auto.routeplanbar.title.bruk_gps_posisjon", "Bruk GPS-posisjon")}
                className="shrink-0 p-2 rounded-xl bg-[var(--bg-surface-alt)] hover:bg-brand/10 text-[var(--text-muted)] hover:text-brand border border-[var(--border-secondary)] transition shadow-sm"
              >
                <LocateFixed size={14} />
              </button>
            )}
          </div>

          {/* Stopp (draggable) */}
          {waypoints.map((wp, i) => (
            <div
              key={i}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={() => handleDrop(i)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => {
                touchStartY.current = e.touches[0].clientY;
                touchStartIndex.current = i;
                setDragIndex(i);
              }}
              onTouchMove={(e) => {
                if (touchStartY.current !== null) handleTouchMove(i, touchStartY.current, e);
              }}
              onTouchEnd={() => handleTouchEnd(i)}
              className={`flex items-center gap-2 transition ${
                dragOverIndex === i && dragIndex !== i ? 'border-t-2 border-amber-400' : ''
              } ${dragIndex === i ? 'opacity-50' : ''}`}
            >
              <div className="w-5 flex justify-center shrink-0">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500 border-2 border-[var(--bg-surface)] shadow" />
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-1.5 px-2.5 py-2 bg-amber-500/15 text-amber-600 dark:text-amber-400 rounded-xl text-tiny font-black">
                <GripVertical size={11} className="shrink-0 text-amber-400/60 cursor-grab" />
                {onWaypointTap ? (
                  <button
                    type="button"
                    onClick={() => onWaypointTap(i)}
                    onTouchStart={(e) => e.stopPropagation()}
                    className="truncate flex-1 text-left hover:underline"
                    title="Vis plassen"
                  >
                    {wp.name.split(',')[0]}
                  </button>
                ) : (
                  <span className="truncate flex-1">{wp.name.split(',')[0]}</span>
                )}
                {/* stopPropagation på touch/pointer: raden er draggable m/ touch-handlers som
                    ellers spiser tappet (10px mål + drag-init = «død» knapp på mobil). */}
                <button
                  type="button"
                  aria-label={t('routePlanner.remove_stop', 'Fjern stopp')}
                  onClick={(e) => { e.stopPropagation(); onRemoveWaypoint(i); }}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className="shrink-0 -my-1.5 ml-0.5 grid h-9 w-9 place-items-center rounded-full hover:text-red-500 transition-colors">
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}

          {/* + Legg til stopp */}
          <div className="flex items-center gap-2">
            <div className="w-5 flex justify-center shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--text-muted)]" />
            </div>
            <button onClick={() => openSearch('stop')}
              className="flex items-center gap-1 px-2.5 py-2 rounded-xl border border-dashed border-[var(--border-secondary)] text-[var(--text-muted)] hover:border-emerald-400 hover:text-brand text-tiny font-bold transition-colors">
              <Plus size={10} /> {t('routePlanner.add_stop_btn', 'Legg til stopp')}
            </button>
          </div>

          {/* Mål (clickable to change destination — always last) */}
          <div className="flex items-center gap-2 group">
            <div className="w-5 flex justify-center shrink-0">
              <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-[var(--bg-surface)] shadow" />
            </div>
            <button
              onClick={() => openSearch('to')}
              className="flex-1 min-w-0 flex items-center justify-between px-3 py-2 bg-red-500/15 hover:bg-red-500/25 border border-transparent hover:border-red-500/50 rounded-xl text-left transition group shadow-sm"
              title={t("auto.routeplanbar.title.klikk_for_a_endre_reisemal", "Klikk for å endre reisemål")}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <MapPin size={12} className="text-red-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-3xs font-black text-red-600/70 dark:text-red-400/70 uppercase tracking-widest leading-none mb-0.5">
                    {t('routePlanner.to_label', 'Til (Reisemål)')}
                  </div>
                  <div className="text-xs font-bold text-red-600 dark:text-red-400 truncate">
                    {toName.split(',')[0]}
                  </div>
                </div>
              </div>
              <span className="shrink-0 ml-2 px-2 py-1 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 text-2xs font-bold group-hover:bg-red-500/20 transition-colors">
                {t("auto.routeplanbar.endre", "Endre")}
              </span>
            </button>
          </div>
        </div>

        {/* Søk (shared for from + stop) */}
        {searchTarget && (
          <div className="px-4 pb-2 relative">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                autoFocus
                placeholder={searchTarget === 'from'
                  ? t('routePlanner.search_from', 'Søk etter avreisested...')
                  : searchTarget === 'to'
                    ? t('routePlanner.search_to', 'Søk etter reisemål...')
                    : t('routePlanner.search_stop', 'Søk etter stopp...')}
                className="w-full pl-8 pr-8 py-2 text-xs font-semibold text-[var(--text-primary)] placeholder:text-[var(--text-muted)] rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-input)] focus:outline-none focus:border-emerald-400"
              />
              <button onClick={() => setSearchTarget(null)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]">
                <X size={14} />
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="absolute left-4 right-4 top-full mt-1 bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-xl shadow-lg z-20 max-h-72 overflow-y-auto overscroll-contain">
                {searchResults.slice(0, 8).map((r, i) => (
                  <button key={i} onClick={() => selectSearchResult(r)}
                    className="flex min-h-[44px] w-full items-center text-left px-3 py-2.5 hover:bg-brand/10 border-b border-[var(--border-primary)] last:border-0 text-sm text-[var(--text-primary)]">
                    <span className="truncate">{r.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hint tekst */}
        {!fetching && alternatives.length > 0 && waypoints.length === 0 && !searchTarget && (
          <p className="px-4 pb-1 text-2xs text-[var(--text-secondary)] font-semibold">
            {t('routePlanner.add_stop', 'Trykk i kartet for å legge til stopp')}
          </p>
        )}

        {/* Loading */}
        {fetching && (
          <div className="flex items-center gap-2 px-4 py-3">
            <Loader2 size={14} className="animate-spin text-brand" />
            <span className="text-xs text-[var(--text-secondary)] font-bold animate-pulse">
              {t('routePlanner.planning', 'Henter ruteforslag...')}
            </span>
          </div>
        )}

        {/* Feil */}
        {error && (
          <div className="mx-4 mb-2 p-2.5 bg-red-500/15 rounded-xl text-tiny text-red-500 font-bold">
            {error}
          </div>
        )}

        {/* Rutekort — kun uten stopp (flere alternativer) */}
        {!fetching && hasAlternatives && (
          <div className="flex gap-2 px-4 pb-2 overflow-x-auto no-scrollbar">
            {alternatives.map((alt, i) => {
              const isSelected = selectedIndex === i;
              const color = ROUTE_COLORS[i] || ROUTE_COLORS[0];
              return (
                <button key={alt.index} onClick={() => onSelectIndex(i)}
                  className={`shrink-0 flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition ${
                    isSelected
                      ? 'border-brand bg-brand/10'
                      : 'border-[var(--border-primary)] bg-[var(--bg-surface-alt)] hover:border-[var(--border-secondary)]'
                  }`}>
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <div className="flex items-center gap-2 text-tiny text-[var(--text-primary)] font-black whitespace-nowrap">
                    <span className="flex items-center gap-0.5">
                      <RouteIcon size={11} className="text-[var(--text-muted)]" />
                      {(alt.distanceMeters / 1000).toFixed(0)} km
                    </span>
                    <span className="flex items-center gap-0.5">
                      <Clock size={11} className="text-[var(--text-muted)]" />
                      {formatDuration(alt.durationSeconds)}
                    </span>
                  </div>
                  {(alt.barrierWarnings || []).some(w => w.isLocked) && (
                    <span className="flex items-center gap-0.5 text-3xs font-bold text-red-500" title={t('routePlanner.locked_barrier_on_route', 'Låst bom langs ruten')}>
                      <AlertTriangle size={10} /> {t('routePlanner.barrier_short', 'Bom')}
                    </span>
                  )}
                  {isSelected && <Check size={14} className="text-brand" />}
                </button>
              );
            })}
          </div>
        )}

        {/* Enkelt rute-info (med stopp, bare 1 rute) */}
        {!fetching && !hasAlternatives && selectedAlt && (
          <div className="flex items-center gap-3 px-4 pb-2 text-xs text-[var(--text-primary)] font-black">
            <span className="flex items-center gap-1">
              <RouteIcon size={13} className="text-[var(--text-secondary)]" />
              {(selectedAlt.distanceMeters / 1000).toFixed(0)} km
            </span>
            <span className="flex items-center gap-1">
              <Clock size={13} className="text-[var(--text-secondary)]" />
              {formatDuration(selectedAlt.durationSeconds)}
            </span>
          </div>
        )}

        {/* Lagre-seksjon */}
        {selectedIndex !== null && !fetching && (
          <div className="px-4 pb-3 border-t border-[var(--border-primary)] pt-2 space-y-2">
            {!saved && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={routeName}
                  onChange={e => setRouteName(e.target.value)}
                  placeholder={t('routePlanner.route_name', 'Navn på ruten')}
                  className="flex-1 min-w-0 px-3 py-2.5 text-xs font-bold text-[var(--text-primary)] placeholder:text-[var(--text-muted)] rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-input)] focus:outline-none focus:border-emerald-400"
                />
                <button onClick={handleSave} disabled={saving}
                  className="shrink-0 px-4 py-2.5 bg-[#444] text-white rounded-xl text-xs font-black hover:bg-[#555] disabled:opacity-60 transition-colors flex items-center gap-1.5 border border-[var(--border-secondary)]">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {t('routePlanner.save_short', 'Lagre')}
                </button>
              </div>
            )}
            {/* Copilot-ruter (onExploreContent): UTFORSK er primærhandling — brukeren skal
                orientere seg i innholdet (stopp/severdigheter/vær), ikke kastes inn i
                GPS-sporing. Navigasjon demoteres til sekundær. Manuelle ruter: som før. */}
            {onExploreContent && (
              <button
                onClick={onExploreContent}
                className="w-full py-3 bg-brand-dark hover:bg-emerald-700 text-white rounded-xl text-xs font-black transition-colors flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20"
              >
                <PanelLeftOpen size={14} />
                {t('routePlanner.explore_route', 'Utforsk ruten')}
              </button>
            )}
            <button
              onClick={() => selectedIndex !== null && onNavigate(alternatives[selectedIndex])}
              className={onExploreContent
                ? 'w-full py-3 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-input)] text-[var(--text-primary)] rounded-xl text-xs font-black transition-colors flex items-center justify-center gap-2 border border-[var(--border-secondary)]'
                : `w-full py-3 ${saved ? 'bg-amber-600 hover:bg-amber-700' : 'bg-brand-dark hover:bg-emerald-700'} text-white rounded-xl text-xs font-black transition-colors flex items-center justify-center gap-2 shadow-lg ${saved ? 'shadow-amber-900/20' : 'shadow-emerald-900/20'}`}
            >
              <Navigation size={14} />
              {t('routePlanner.start_navigation', 'Start Navigering')}
            </button>
            {onShowRouteContent && !onExploreContent && (
              <button
                onClick={onShowRouteContent}
                className="w-full py-3 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-input)] text-[var(--text-primary)] rounded-xl text-xs font-black transition-colors flex items-center justify-center gap-2 border border-[var(--border-secondary)]"
              >
                <PanelLeftOpen size={14} />
                {t('routePlanner.show_route_content', 'Vis ruteinnhold')}
              </button>
            )}
          </div>
        )}

        {/* Lagret-bekreftelse */}
        {saved && (
          <div className="flex items-center gap-2 px-4 pb-3 pt-0 text-2xs text-brand font-black">
            <Check size={12} />
            {t('routePlanner.saved', 'Rute lagret! Du finner den under Mine turer.')}
          </div>
        )}

        </div>{/* closes scrollable content area */}
      </div>
      )}
    </div>
  );
};

export default RoutePlanBar;
